/* DAILE v2 (dv) — glass optics. Port of the MVP glass-optics.js.
 *
 * Every element marked [data-dv-glass] (a pill or circle: radius = height/2)
 * gets a <span class="dv-glass-material"> prepended and the class
 * .dv-glass-mounted. In Chromium an SVG feDisplacementMap filter refracts the
 * rim of the backdrop while the centre is frosted; the maps are regenerated
 * only when the element changes size (ResizeObserver). Other browsers, and
 * reduced transparency / increased contrast, keep the CSS blur or solid glass
 * defined in dv-base.css.
 *
 * Optional value: data-dv-glass="32" = frost stdDeviation (MVP header 32,
 * NFC tabs 27; default 27).
 *
 * Also sets html[data-dv-glass-optics="refraction|blur"] and
 * html.dv-opaque-glass (reduced transparency or increased contrast).
 */
(() => {
  'use strict';

  if (!window.DV || typeof window.DV.register !== 'function') return;

  const doc = document;
  const html = doc.documentElement;
  const ns = 'http://www.w3.org/2000/svg';
  const svgEl = (name, attrs = {}) => {
    const node = doc.createElementNS(ns, name);
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, String(value)));
    return node;
  };

  const reduceTransparency = matchMedia('(prefers-reduced-transparency: reduce)');
  const highContrast = matchMedia('(prefers-contrast: more)');
  const isOpaque = () => reduceTransparency.matches || highContrast.matches;

  // WebKit can parse url() without rendering SVG backdrop reference filters,
  // so refraction is limited to Chromium; everything else keeps the CSS blur.
  const canRefract = /Chrome|Chromium|Edg\//.test(navigator.userAgent)
    && !!(window.CSS && CSS.supports && CSS.supports('backdrop-filter', 'url("#dv-glass-probe")'))
    && 'ResizeObserver' in window;

  html.setAttribute('data-dv-glass-optics', canRefract ? 'refraction' : 'blur');

  const rebuilders = new Set();
  const syncPreference = () => {
    html.classList.toggle('dv-opaque-glass', isOpaque());
    rebuilders.forEach((rebuild) => rebuild(true));
  };
  window.DV.onMediaChange(reduceTransparency, syncPreference);
  window.DV.onMediaChange(highContrast, syncPreference);
  html.classList.toggle('dv-opaque-glass', isOpaque());

  let defs = null;
  const ensureDefs = () => {
    if (defs && defs.isConnected) return defs;
    const svg = svgEl('svg', { 'aria-hidden': 'true', focusable: 'false', width: 0, height: 0 });
    svg.classList.add('dv-glass-filter-defs');
    defs = svgEl('defs');
    svg.append(defs);
    doc.body.append(svg);
    return defs;
  };

  let counter = 0;

  const mount = (target) => {
    let material = [...target.children].find((child) => child.classList.contains('dv-glass-material'));
    const injected = !material;
    if (injected) {
      material = doc.createElement('span');
      material.className = 'dv-glass-material';
      material.setAttribute('aria-hidden', 'true');
      target.prepend(material);
    }
    target.classList.add('dv-glass-mounted');

    const unmount = () => {
      target.classList.remove('dv-glass-mounted');
      if (injected) material.remove();
      else material.style.removeProperty('--dv-optical-filter');
    };

    if (!canRefract) return unmount;

    const id = `dv-optics-${++counter}`;
    const frost = Number.parseFloat(target.getAttribute('data-dv-glass')) || 27;
    const filter = svgEl('filter', { id, x: 0, y: 0, filterUnits: 'userSpaceOnUse', 'color-interpolation-filters': 'sRGB' });
    const vector = svgEl('feImage', { x: 0, y: 0, preserveAspectRatio: 'none', result: 'vector' });
    const rim = svgEl('feImage', { x: 0, y: 0, preserveAspectRatio: 'none', result: 'rim' });
    filter.append(
      vector,
      svgEl('feDisplacementMap', { in: 'SourceGraphic', in2: 'vector', scale: 28, xChannelSelector: 'R', yChannelSelector: 'G', result: 'refracted' }),
      svgEl('feGaussianBlur', { in: 'refracted', stdDeviation: 0.65, result: 'polished' }),
      rim,
      svgEl('feGaussianBlur', { in: 'SourceGraphic', stdDeviation: frost, result: 'frosted' }),
      svgEl('feComposite', { in: 'polished', in2: 'rim', operator: 'in', result: 'edge' }),
      svgEl('feComposite', { in: 'frosted', in2: 'rim', operator: 'out', result: 'centre' }),
    );
    const merge = svgEl('feMerge');
    merge.append(svgEl('feMergeNode', { in: 'centre' }), svgEl('feMergeNode', { in: 'edge' }));
    filter.append(merge);
    ensureDefs().append(filter);

    let previousSize = '';
    const rebuild = (force) => {
      if (isOpaque()) {
        material.style.removeProperty('--dv-optical-filter');
        previousSize = '';
        return;
      }
      const w = Math.round(target.clientWidth);
      const h = Math.round(target.clientHeight);
      if (!w || !h || (!force && `${w}:${h}` === previousSize)) return;
      previousSize = `${w}:${h}`;
      const canvas = doc.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const context = canvas.getContext('2d');
      if (!context) return;
      const displacement = context.createImageData(w, h);
      const edge = context.createImageData(w, h);
      const radius = h / 2;
      const ring = Math.min(12, h * 0.2);
      for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1) {
          const cx = Math.max(radius, Math.min(w - radius, x + 0.5));
          const dx = x + 0.5 - cx;
          const dy = y + 0.5 - radius;
          const distance = Math.hypot(dx, dy);
          const depth = radius - distance;
          const t = Math.max(0, Math.min(1, depth / ring));
          const weight = depth >= 0 ? (1 - t) ** 2 : 0;
          const i = (y * w + x) * 4;
          displacement.data[i] = 128 - (distance ? dx / distance : 0) * 124 * weight;
          displacement.data[i + 1] = 128 - (distance ? dy / distance : 0) * 124 * weight;
          displacement.data[i + 2] = 128;
          displacement.data[i + 3] = 255;
          // Smoothly blend the sharp refracting lip into the softer centre.
          const blend = Math.max(0, Math.min(1, (t - 0.38) / 0.62));
          edge.data[i] = 255;
          edge.data[i + 1] = 255;
          edge.data[i + 2] = 255;
          edge.data[i + 3] = depth >= 0 ? Math.round(255 * (1 - blend * blend * (3 - 2 * blend))) : 0;
        }
      }
      context.putImageData(displacement, 0, 0);
      vector.setAttribute('href', canvas.toDataURL());
      context.putImageData(edge, 0, 0);
      rim.setAttribute('href', canvas.toDataURL());
      [filter, vector, rim].forEach((node) => {
        node.setAttribute('width', w);
        node.setAttribute('height', h);
      });
      material.style.setProperty('--dv-optical-filter', `url("#${id}")`);
    };

    const observer = new ResizeObserver(() => rebuild(false));
    observer.observe(target);
    rebuild(false);
    rebuilders.add(rebuild);

    return () => {
      observer.disconnect();
      rebuilders.delete(rebuild);
      filter.remove();
      unmount();
    };
  };

  window.DV.register('dv-glass', (root) => {
    const cleanups = window.DV.queryAll(root, '[data-dv-glass]').map(mount);
    if (!cleanups.length) return undefined;
    return () => cleanups.forEach((fn) => fn());
  });
})();
